import { useMutation } from 'react-query'
import { SUCCESS_RESPONSE_TYPE } from 'api/constants'
import { useCurrentWorkspaceId } from 'hooks/useCurrentWorkspaceId'
import { sendToApi } from 'api/client'
import { UseMutationOptions } from 'react-query/types/react/types'
import { reportError } from 'utils/error'
import { buildErrorMessage } from 'utils'
import { Workspace } from 'types'
import { showErrorMessageToast } from 'common/MessageToasts/ErrorMessageToast/ErrorMessageToast'
import { WorkspaceService } from 'api/services/WorkspaceService'

interface ToggleAutopilotParams {
  workspaceId: number
  pauseAutopilotGenerateAt: string | null | undefined
  pauseAutopilotPublishAt: string | null | undefined
  resumeAutopilotGenerateAt: string | null | undefined
  resumeAutopilotPublishAt: string | null | undefined
}

type MutationParams = Omit<ToggleAutopilotParams, 'workspaceId'>

export type Options = Omit<UseMutationOptions<Workspace, unknown, MutationParams>, 'mutationKey' | 'mutationFn'>

export const toggleAutopilot = async ({
  workspaceId,
  pauseAutopilotGenerateAt,
  pauseAutopilotPublishAt,
  resumeAutopilotGenerateAt,
  resumeAutopilotPublishAt,
}: ToggleAutopilotParams) => {
  const response = await sendToApi<Workspace>(`/w/${workspaceId}/update`, {
    method: 'PUT',
    body: {
      pause_autopilot_generate_at: pauseAutopilotGenerateAt,
      pause_autopilot_publish_at: pauseAutopilotPublishAt,
      resume_autopilot_generate_at: resumeAutopilotGenerateAt,
      resume_autopilot_publish_at: resumeAutopilotPublishAt,
    },
  })

  if (response.type !== SUCCESS_RESPONSE_TYPE) {
    reportError('Failed to toggle autopilot', {
      metaData: {
        workspaceId,
        pauseAutopilotGenerateAt,
        pauseAutopilotPublishAt,
        response: response.data,
      },
    })
    throw new Error(
      buildErrorMessage(response.data.errors) ||
      'An error occurred! Our team has been notified and we are looking into it.',
    )
  }

  return response.data
}

export const useToggleAutopilot = (options?: Options) => {
  const currentWorkspaceId = useCurrentWorkspaceId()

  return useMutation(
    ['workspaces', currentWorkspaceId, 'toggleAutopilot'],
    (params: MutationParams) => toggleAutopilot({ workspaceId: currentWorkspaceId, ...params }),
    {
      onSuccess: () => {
        WorkspaceService.getWorkspaces()
      },
      onError: () => {
        showErrorMessageToast("We couldn't update autopilot settings. Please try again.")
      },
      ...options,
    },
  )
}
